import { type DragEvent, useRef, useState } from "react"

const FILE_DRAG_TYPE = "Files"

export interface FileDropSurfaceOptions {
  onFilesDropped: (files: File[]) => void
  isEnabled: boolean
}

export interface FileDropSurfaceHandlers {
  onDragEnter: (event: DragEvent<HTMLElement>) => void
  onDragOver: (event: DragEvent<HTMLElement>) => void
  onDragLeave: (event: DragEvent<HTMLElement>) => void
  onDrop: (event: DragEvent<HTMLElement>) => void
}

export interface FileDropSurface {
  isDraggingFiles: boolean
  surfaceHandlers: FileDropSurfaceHandlers
}

function isFileDrag(event: DragEvent<HTMLElement>): boolean {
  return event.dataTransfer.types.includes(FILE_DRAG_TYPE)
}

export function useFileDropSurface({ onFilesDropped, isEnabled }: FileDropSurfaceOptions): FileDropSurface {
  const [isFileDragOverSurface, setIsFileDragOverSurface] = useState(false)
  const fileDragEnterDepthRef = useRef(0)

  const surfaceHandlers: FileDropSurfaceHandlers = {
    onDragEnter(event) {
      if (!isFileDrag(event)) return
      event.preventDefault()
      fileDragEnterDepthRef.current += 1
      setIsFileDragOverSurface(true)
    },
    onDragOver(event) {
      if (!isFileDrag(event)) return
      event.preventDefault()
    },
    onDragLeave(event) {
      if (!isFileDrag(event)) return
      fileDragEnterDepthRef.current = Math.max(0, fileDragEnterDepthRef.current - 1)
      if (fileDragEnterDepthRef.current === 0) setIsFileDragOverSurface(false)
    },
    onDrop(event) {
      if (!isFileDrag(event)) return
      const isClaimedByNestedDropZone = event.defaultPrevented
      event.preventDefault()
      fileDragEnterDepthRef.current = 0
      setIsFileDragOverSurface(false)
      const droppedFiles = Array.from(event.dataTransfer.files)
      if (!isEnabled || isClaimedByNestedDropZone || droppedFiles.length === 0) return
      onFilesDropped(droppedFiles)
    },
  }

  return { isDraggingFiles: isEnabled && isFileDragOverSurface, surfaceHandlers }
}
